import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { LangService } from '../../services/lang.service';
import { UserService } from '../../services/user.service';
import { ResourceItem, ResourceType } from '../../structure/types/resource-item.class';
import { Guide } from '../../structure/types/guide.class';
import { navigateToGuide } from '../../content/guides/guide-list.utils';

@Component({
    selector: 'app-resource-card',
    templateUrl: './resource-card.component.html',
    styleUrls: ['./resource-card.component.scss'],
    standalone: false
})
export class ResourceCardComponent {
    @Input() resource!: ResourceItem;

    constructor(public lang: LangService, private router: Router, private userService: UserService) {}

    private static readonly ROUTE_BY_TYPE: Record<ResourceType, string> = {
        article: '/article',
        guide: '/guide',
        short: '/short',
        audio: '/audio',
        video: '/video',
    };

    private static readonly TYPE_FALLBACK: Record<ResourceType, string> = {
        article: 'Article',
        guide: 'Guide',
        short: 'Short',
        audio: 'Audio',
        video: 'Video',
    };

    /** The human-readable resource type shown on the card (translatable). */
    get typeLabel(): string {
        return this.lang.data['resources.type_' + this.resource.type]
            || ResourceCardComponent.TYPE_FALLBACK[this.resource.type]
            || this.resource.type;
    }

    /**
     * Guides carry progress; the other types don't. A started guide shows the badge even at
     * 0% (step 0) so it reads as "in progress" in the "Continue where you left off" strip.
     */
    get showProgress(): boolean {
        return this.resource.type === 'guide'
            && (this.resource.status === 'in_progress' || this.resource.status === 'completed');
    }

    get isCompleted(): boolean {
        return this.resource.status === 'completed';
    }

    async open() {
        // Guides have no slug and their entry point depends on status (preview when
        // not started, retake when completed) — reuse the canonical guide navigation.
        if (this.resource.type === 'guide') {
            // Guests can browse guides in the listings but must log in to open one.
            if (!this.userService.isLoggedIn) {
                await this.router.navigate(['/login']);
                return;
            }
            await navigateToGuide(this.router, {
                id: this.resource.id,
                status: this.resource.status,
            } as Guide);
            return;
        }

        const base = ResourceCardComponent.ROUTE_BY_TYPE[this.resource.type] ?? '/article';
        await this.router.navigate([base, this.resource.slug]);
    }
}
